/**
 * Swaps AssetsKit storage images that fail to load (direct or through the
 * Netlify image CDN) for their local public/ copy. Covers CDN-cached HTML
 * rendered while AssetsKit storage was still reachable.
 */
const FALLBACK_ATTR = 'data-assetskit-fallback'

export default defineNuxtPlugin(() => {
  let fallbackModule: Promise<typeof import('~/utils/assetskitMediaFallback')> | null = null
  const loadFallback = () => {
    fallbackModule ||= import('~/utils/assetskitMediaFallback')
    return fallbackModule
  }

  const failedSource = (img: HTMLImageElement) => img.currentSrc || img.getAttribute('src') || ''

  const handleError = async (event: Event) => {
    const img = event.target
    if (!(img instanceof HTMLImageElement) || img.hasAttribute(FALLBACK_ATTR)) return

    const source = failedSource(img)
    if (!source.includes('supabase.co')) return

    const { extractAssetsKitStorageUrl, localAssetsKitImageOrGeneric } = await loadFallback()
    const storageUrl = extractAssetsKitStorageUrl(source) ||
      extractAssetsKitStorageUrl(img.getAttribute('srcset'))
    if (!storageUrl) return

    img.setAttribute(FALLBACK_ATTR, '1')
    const picture = img.parentElement instanceof HTMLPictureElement ? img.parentElement : null
    picture?.querySelectorAll('source').forEach(node => node.removeAttribute('srcset'))
    img.removeAttribute('srcset')
    img.removeAttribute('sizes')
    img.src = localAssetsKitImageOrGeneric(storageUrl)
  }

  // Image errors do not bubble: listen in the capture phase.
  window.addEventListener('error', (event) => { void handleError(event) }, true)

  // Images that already failed before hydration.
  const sweep = () => {
    document.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
      if (img.complete && img.naturalWidth === 0 && failedSource(img).includes('supabase.co')) {
        void handleError({ target: img } as unknown as Event)
      }
    })
  }
  if (document.readyState === 'complete') sweep()
  else window.addEventListener('load', sweep, { once: true })
})
